import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Pencil, Send, X } from "lucide-react";
import api from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-orange focus:border-nc-orange";
const labelCls = "font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";
const iconBtnCls = "border border-white/10 p-1.5 text-zinc-400 hover:text-zinc-100 hover:border-white/30 transition-colors duration-300";

export default function AdminDevlog() {
  const [posts, setPosts] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [image, setImage] = useState("");
  const [category, setCategory] = useState("new-citizen");
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/devlog").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const resetForm = () => { setTitle(""); setBody(""); setImage(""); setCategory("new-citizen"); setEditingId(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/devlog/${editingId}`, { title, body, image, category });
        toast.success("Transmission updated");
      } else {
        await api.post("/devlog", { title, body, image, category });
        toast.success("Transmission posted");
      }
      resetForm();
      load();
    } catch {
      toast.error("Failed to save transmission");
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = (post) => { setEditingId(post.id); setTitle(post.title); setBody(post.body); setImage(post.image || ""); setCategory(post.category || "new-citizen"); };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/devlog/${id}`);
      toast.success("Transmission deleted");
      if (editingId === id) resetForm();
      load();
    } catch {
      toast.error("Failed to delete transmission");
    }
  };

  return (
    <section data-testid="admin-devlog-section" className="border border-white/10 bg-black/40">
      <div className="border-b border-white/10 px-6 py-4">
        <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-nc-orange">Devlog // Transmissions</h2>
      </div>
      <div className="p-6 flex flex-col gap-6">
        <form onSubmit={handleSubmit} className="border border-white/10 p-4 flex flex-col gap-3 bg-[#050505]/50">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Title</label>
              <input data-testid="admin-devlog-title-input" className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} required />
            </div>
            <div>
              <label className={labelCls}>Channel</label>
              <select data-testid="admin-devlog-category-select" className={inputCls} value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="new-citizen">New Citizen</option>
                <option value="company">Company</option>
                <option value="asset-packs">Asset Packs</option>
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Body — supports **bold**, lists, [links](url)</label>
            <textarea data-testid="admin-devlog-body-input" className={`${inputCls} resize-none`} rows={5} value={body} onChange={(e) => setBody(e.target.value)} required />
          </div>
          <div>
            <label className={labelCls}>Image URL (optional)</label>
            <input data-testid="admin-devlog-image-input" className={inputCls} value={image} onChange={(e) => setImage(e.target.value)} placeholder="https://i.imgur.com/..." />
            {image && <img src={image} alt="Preview" className="mt-3 max-h-40 border border-white/10 object-cover" />}
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-devlog-submit-button"
              type="submit"
              disabled={busy}
              className="font-sans font-semibold text-sm uppercase tracking-[0.15em] bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-50 text-white px-5 py-2 flex items-center gap-2 transition-colors duration-300"
            >
              <Send className="w-4 h-4" /> {editingId ? "Save Changes" : "Post Update"}
            </button>
            {editingId && (
              <button
                data-testid="admin-devlog-cancel-edit-button"
                type="button"
                onClick={resetForm}
                className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-zinc-100 border border-white/10 px-3 py-2 flex items-center gap-2 transition-colors duration-300"
              >
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
            )}
          </div>
        </form>

        <div className="flex flex-col gap-3">
          {posts.length === 0 && (
            <p data-testid="admin-devlog-empty" className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase">No transmissions yet.</p>
          )}
          {posts.map((post, i) => (
            <div key={post.id} data-testid={`admin-devlog-post-${i + 1}`} className="border border-white/10 p-4 bg-[#050505]/50 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-nc-blue border border-nc-blue/40 px-2 py-0.5">{post.category || "new-citizen"}</span>
                  <h3 className="font-sans font-bold uppercase text-base text-zinc-100">{post.title}</h3>
                  <span className="font-mono text-[10px] tracking-[0.25em] text-zinc-500">{String(post.createdAt).slice(0, 10)}</span>
                </div>
                {post.image && <img src={post.image} alt="" className="mt-3 max-h-24 border border-white/10 object-cover" />}
                <p className="font-mono text-xs text-zinc-500 mt-2 whitespace-pre-wrap line-clamp-3">{post.body}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid={`admin-devlog-post-${i + 1}-edit-button`} onClick={() => handleEdit(post)} className={iconBtnCls} title="Edit">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button data-testid={`admin-devlog-post-${i + 1}-delete-button`} onClick={() => handleDelete(post.id)} className={`${iconBtnCls} hover:text-red-400`} title="Delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
